import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useIsBillingManager } from "@/hooks/use-billing-access";
import { authClient } from "@/lib/auth-client";

/**
 * Gates a route on Billing manager access for the active organization. Unlike
 * `OrgAdminRoute`, this does not collapse to `owner/admin` only — it defers to
 * `hasOrganizationBillingManagerAccess`, so a plain member can reach `/billing`
 * when the org has configured `billingManagedByRole = "member"` (ADR-0007
 * "Billing exception").
 */
export function BillingManagerRoute({ children }: { children: ReactNode }) {
  const { data: session, isPending } = authClient.useSession();
  const isBillingManager = useIsBillingManager();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPending || session) return;
    navigate("/auth/restaurant/sign-in");
  }, [session, isPending, navigate]);

  useEffect(() => {
    if (isPending || !session || isBillingManager) return;
    navigate("/");
  }, [session, isPending, isBillingManager, navigate]);

  if (isPending) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (session && isBillingManager) {
    return <>{children}</>;
  }

  return null;
}
